import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getHotMovies } from '@/utils/movie';
import { formatViews } from '@/utils/movie';

const featured = getHotMovies(24).filter((movie) => Boolean(movie.backdrop)).slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused]);

  const goTo = (next: number) => setIndex((next + featured.length) % featured.length);
  const current = featured[index];

  return (
    <section
      className="relative w-full h-[480px] md:h-[640px] overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {featured.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={movie.backdrop || movie.cover}
            alt={`${movie.title} 焦点图`}
            title={`${movie.title} - 亚洲中文字幕在线观看`}
            className={`w-full h-full object-cover object-top ${i === index ? 'animate-kenburns' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/70 to-background-950/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 leading-tight">
            亚洲中文字幕在线观看
          </h1>
          <p className="mt-2 text-sm md:text-base text-primary-400">
            高清亚洲影视 · 全站中文字幕 · 随心在线观看
          </p>

          <div className="mt-4 md:mt-6 border-t border-background-50/10 pt-4 md:pt-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 h-6 flex items-center rounded-md bg-primary-500 text-background-950 text-xs font-medium">
                精选推荐
              </span>
              <span className="px-2.5 h-6 flex items-center rounded-md bg-background-50/10 text-foreground-800 text-xs backdrop-blur">
                {current.region} · {current.year}
              </span>
              <span className="px-2.5 h-6 flex items-center rounded-md bg-accent-500/90 text-background-50 text-xs font-label tracking-wide">
                <i className="ri-star-fill mr-1 text-[10px]"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>
            <h2 className="font-heading font-black text-2xl md:text-4xl text-background-50 leading-tight animate-fade-up">
              {current.title}
            </h2>
            <p className="mt-1 text-xs text-foreground-600 font-label tracking-wide">
              {current.originalTitle}
            </p>
            <p className="mt-3 hidden sm:block text-sm text-foreground-700 leading-7 max-w-xl line-clamp-2">
              {current.synopsis}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-700">
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line text-primary-400"></i>
                导演 {current.director}
              </span>
              <span className="text-foreground-400">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-500"></i>
                {formatViews(current.views)} 次播放
              </span>
              <span className="text-foreground-400">|</span>
              <span>{current.duration}</span>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="px-6 h-11 flex items-center gap-2 rounded-md bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/detail/${current.id}`}
                className="px-6 h-11 flex items-center gap-2 rounded-md bg-background-50/10 text-foreground-900 text-sm font-medium hover:bg-background-50/20 cursor-pointer whitespace-nowrap backdrop-blur transition-colors"
              >
                <i className="ri-information-line text-lg"></i>
                影片详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-4 md:bottom-6 left-4 md:left-8 flex items-center gap-3">
        {featured.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full cursor-pointer transition-all ${
              i === index ? 'w-8 bg-primary-500' : 'w-4 bg-background-50/40 hover:bg-background-50/70'
            }`}
          ></button>
        ))}
      </div>

      <div className="absolute z-10 bottom-4 md:bottom-6 right-4 md:right-8 hidden sm:flex items-center gap-2">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一张"
          className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/10 text-foreground-800 hover:bg-background-50/20 backdrop-blur cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一张"
          className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/10 text-foreground-800 hover:bg-background-50/20 backdrop-blur cursor-pointer transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}